import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
} from "react";

import { getUserByToken, useAuthLogout } from "./_requests";
import { WithChildren } from "../../../../_metronic/helpers";
import { LayoutSplashScreen } from "../../../../_metronic/layout/core";
import { UserModel } from "./_models";
import {
  finishLinkLogin,
  setGlobalLogoutHandler,
  setLockUserHandler,
} from "./AuthHelpers";

type AuthContextProps = {
  isAuth: boolean;
  setIsAuth: Dispatch<SetStateAction<boolean>>;
  saveAuth: () => void;
  logout: () => any;
  logoutIfCondition: () => any;
  currentUser: UserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  shouldRefetchUser: boolean;
  setShouldRefetchUser: Dispatch<SetStateAction<boolean>>;
  isLockUser: boolean;
  errorAuth: any;
  setErrorAuth: Dispatch<SetStateAction<any>>;
  userCode: string | null;
  setUserCode: Dispatch<SetStateAction<string | null>>;

  isAdmin: boolean;
  isModerator: boolean;
  isContents: boolean;
  isArbitrator: boolean;
  isManager: boolean;
  isSupport: boolean;
};

const initAuthContextPropsState = {
  isAuth: false,
  setIsAuth: () => {},
  saveAuth: () => {},
  logout: () => {},
  logoutIfCondition: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  shouldRefetchUser: false,
  setShouldRefetchUser: () => {},
  isLockUser: false,
  errorAuth: null,
  setErrorAuth: () => {},
  userCode: null,
  setUserCode: () => {},

  isAdmin: false,
  isModerator: false,
  isContents: false,
  isArbitrator: false,
  isManager: false,
  isSupport: false,
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>(
    undefined
  );
  const [shouldRefetchUser, setShouldRefetchUser] = useState<boolean>(false);
  const { mutateAsync: authLogout } = useAuthLogout();
  const [isLockUser, setIsLockUser] = useState(false);

  const [userCode, setUserCode] = useState<string | null>(null);

  const [errorAuth, setErrorAuth] = useState<any | null>(null);

  function saveAuth() {
    setIsAuth(true);
  }

  // Клиентский сброс без сети — для 401-интерцептора (чтобы не зациклить логаут).
  function forceLogout() {
    setCurrentUser(undefined);
    setIsAuth(false);
  }

  // Пользовательский логаут: отзываем сессию на сервере, затем чистим клиент.
  async function logout() {
    try {
      await authLogout();
    } catch {
      /* даже при ошибке сбрасываем клиентское состояние */
    }
    forceLogout();
  }

  // Доступ запрещён (роль не подходит) — тоже полноценный логаут.
  function logoutIfCondition() {
    return logout();
  }

  function lockUser() {
    setIsLockUser(true);
    setIsAuth(false);
  }

  useEffect(() => {
    setGlobalLogoutHandler(forceLogout);
    setLockUserHandler(lockUser);
  }, []);

  const isAdmin = currentUser?.roles.includes("admin") || false;
  const isModerator = currentUser?.roles.includes("moderator") || false;
  const isContents = currentUser?.roles.includes("content") || false;
  const isArbitrator = currentUser?.roles.includes("arbitrator") || false;
  const isManager = currentUser?.roles.includes("manager") || false;
  const isSupport = currentUser?.roles.includes("support") || false;

  return (
    <AuthContext.Provider
      value={{
        isAuth,
        setIsAuth,
        saveAuth,
        logout,
        logoutIfCondition,
        currentUser,
        setCurrentUser,
        shouldRefetchUser,
        setShouldRefetchUser,
        isLockUser,
        errorAuth,
        setErrorAuth,
        userCode,
        setUserCode,

        isAdmin,
        isModerator,
        isContents,
        isArbitrator,
        isManager,
        isSupport,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({ children }) => {
  const {
    setCurrentUser,
    saveAuth,
    setErrorAuth,
    logoutIfCondition,
    shouldRefetchUser,
    setShouldRefetchUser,
  } = useAuth();

  const [showSplashScreen, setShowSplashScreen] = useState(true);

  const requestUser = async () => {
    try {
      setErrorAuth(null);

      const data = await getUserByToken();

      // Профиль пришёл — сессия в cookie рабочая, и вход по ссылке (был он или
      // нет) больше ничего не решает. Снимаем гейт здесь, потому что в этой
      // ветке Login не смонтируется и снять его больше некому: иначе окно кода
      // после настоящего простоя не открылось бы до конца жизни вкладки.
      finishLinkLogin();

      const isStaff = data.roles.some(
        (role) =>
          role === "admin" ||
          role === "moderator" ||
          role === "content" ||
          role === "support" ||
          role === "arbitrator" ||
          role === "manager"
      );

      if (isStaff) {
        setCurrentUser(data);
        saveAuth();
      } else {
        setErrorAuth("Ошибка доступа");
        logoutIfCondition();
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setShowSplashScreen(false);
    }
  };

  // Профиль запрашивается при загрузке (валидная cookie → сразу авторизован)
  // и по сигналу из Login после обмена ?code на сессию.
  useEffect(() => {
    requestUser();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (shouldRefetchUser) {
      setShouldRefetchUser(false);
      requestUser();
    }
    // eslint-disable-next-line
  }, [shouldRefetchUser]);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>;
};

export { AuthProvider, useAuth, AuthInit };
